import axios from 'axios';
import Swal from 'sweetalert2';
import { CURR_API } from '@/constants/apis';
import { sessionActorFrom } from '@/utils/sessionIdentity';
import { peekSessionSelfProfile } from '@/stores/sessionSelf';
import { changedCourseDate } from '@/utils/medicationCourseDates';

const FUTURE_DAYS_TO_POPULATE = 365;

export function normalizeToMidnight(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function formatDate(date) {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDateToYYYYMMDD(d) {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}

export function isOrderedMed(med) {
  if (!med) return false;
  const orderNumber = med.order_number ?? med.ordernumber;
  return orderNumber != null && orderNumber !== 0 && orderNumber !== '0';
}

function normalizeNwtime(nwtime) {
  if (nwtime == null) return [];
  if (typeof nwtime === 'object' && 'value' in nwtime) {
    return Array.isArray(nwtime.value) ? nwtime.value : [];
  }
  return Array.isArray(nwtime) ? nwtime : [];
}

export function buildHoldTimes(med) {
  if (!med) return [];
  const timesSet = new Set();
  if (med.dates) {
    for (const dateKey in med.dates) {
      med.dates[dateKey].forEach((tObj) => {
        if (tObj?.time) timesSet.add(tObj.time);
      });
    }
  }
  if (timesSet.size === 0 && med.administrationTimes && med.administrationTimes !== 'As needed') {
    med.administrationTimes
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((time) => timesSet.add(time));
  }
  return Array.from(timesSet);
}

export function checkMedHoldDatesUpdate(editFormdata, holdStart, holdEnd) {
  if (
    editFormdata &&
    (editFormdata.medholdstartdate !== holdStart || editFormdata.medholdenddate !== holdEnd)
  ) {
    return { changeActiveMed: true, ifStatusIsChange: true };
  }
  return { changeActiveMed: false, ifStatusIsChange: false };
}

export function checkMedActiveStatus(editFormdata, frequency, currentDosage) {
  if (editFormdata && editFormdata.med_frequency !== frequency) {
    return { changeActiveMed: true, ifStatusIsChange: true };
  }
  if (editFormdata && editFormdata.med_frequency === frequency && editFormdata.med_amount == currentDosage) {
    return { changeActiveMed: false, ifStatusIsChange: false };
  }
  return null;
}

export function checkMedActiveDosageStatus(editFormdata, dosage, currentFrequency) {
  if (editFormdata && editFormdata.med_amount != dosage) {
    return { changeActiveMed: true, ifStatusIsChange: true };
  }
  if (editFormdata && editFormdata.med_amount == dosage && editFormdata.med_frequency === currentFrequency) {
    return { changeActiveMed: false, ifStatusIsChange: false };
  }
  return null;
}

export async function fetchMedAdminRecord(allData, gridMed) {
  // Prefer order-scoped edit lookup so CurrentMed meds without Administration still
  // return paproviders / patpharmacy (GetPatientMeds filters medadmin=1).
  const editContent = {
    MedicationAdmin: {
      API_Meth: 'GetOrderedMedEditInfo',
      pid: allData.patient.externId,
      accountId: allData.patient.externId2,
      medentryid: gridMed.medentryid,
      ordernumber: gridMed.order_number ?? gridMed.ordernumber ?? null,
      provsubaccount: allData.manager.externid,
    },
  };
  try {
    const editResponse = await axios.post(CURR_API + '/tswebhook.php', editContent);
    if (editResponse.data?.record) {
      return editResponse.data.record;
    }
  } catch (err) {
    console.warn('GetOrderedMedEditInfo failed, falling back to GetPatientMeds', err);
  }

  const content = {
    MedicationAdmin: {
      API_Meth: 'GetPatientMeds',
      pid: allData.patient.externId,
      accountId: allData.patient.externId2,
      providerid: '123456789',
      proflicensenumber: '609382',
      provsubaccount: allData.manager.externid,
    },
  };
  const response = await axios.post(CURR_API + '/tswebhook.php', content);
  const records = response.data?.records || [];
  return (
    records.find(
      (r) =>
        String(r.medentryid) === String(gridMed.medentryid) ||
        (String(r.order_number) === String(gridMed.order_number) && r.medname === gridMed.medname)
    ) || null
  );
}

function showSavingSwal(text) {
  return Swal.fire({
    title: 'Saving medication...',
    text,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });
}

function handleSaveSuccess(message, title, onDone) {
  Swal.fire({
    icon: 'success',
    title,
    text: message,
    confirmButtonText: 'Ok',
  }).then(() => {
    onDone?.();
  });
}

function handleSaveError(title, errorMessage) {
  Swal.fire({
    icon: 'error',
    title,
    text: errorMessage,
    confirmButtonText: 'Ok',
  });
}

async function postMedicationAdmin(content) {
  const response = await axios.post(CURR_API + '/tswebhook.php', content);
  return response.data;
}

export async function scheduleHoldMedication(allData, medholddata) {
  const content = {
    MedicationAdmin: {
      API_Meth: 'ScheduleHoldMedication',
      accountnumber: allData.patient.externId2,
      npinumber: allData.manager.externid || '',
      patientid: allData.patient.externId,
      holdobjec: medholddata,
      provsubaccnt: allData.manager.externid,
    },
  };
  const response = await axios.post(CURR_API + '/tswebhook.php', content);
  return response.data;
}

export async function holdMedication(allData, medholddata) {
  const now = new Date();
  const orderDate = now.toISOString().split('T')[0];
  const orderTime = now.toTimeString().split(' ')[0];
  const content = {
    MedicationAdmin: {
      API_Meth: 'HoldMedication',
      accountnumber: allData.patient.externId2,
      npinumber: allData.manager.externid || '',
      patientid: allData.patient.externId,
      holdobjec: medholddata,
      provsubaccnt: allData.manager.externid,
      orderDate,
      orderTime,
    },
  };
  const response = await axios.post(CURR_API + '/tswebhook.php', content);
  return response.data;
}

function buildMedHoldInfo(medication, data, statusOption) {
  const ordernumber = medication.order_number || medication.ordernumber;
  return {
    medname: medication.medname,
    medentryid: medication.medentryid,
    dateRange: data.dateRange,
    times: data.times,
    reason: data.reason,
    type: data.holdType,
    ordernumber,
    status: statusOption,
    archiveHLDDC: data.archiveHLDDC,
    scheduleDates: data.schedDt,
    action: data.action,
  };
}

export function buildHoldSubmitPayload(medication, data) {
  return buildMedHoldInfo(medication, data, data.statusOption);
}

export function buildHoldSchedulePayload(medication, data) {
  return buildMedHoldInfo(medication, data, data.statusOption);
}

/**
 * Save an ordered medication edit using the same MedAdmin APIs and change-order rules:
 * frequency or dosage change triggers ChangeOrderForMedLogTimes; otherwise InsertUpdateMedLogTimes.
 */
export async function saveOrderedMedicationEdit({
  medication,
  nwtime,
  selectedFreq,
  allData,
  changeActiveMed,
  changeReason,
  selectedMedicationForTime,
  timeInputs,
  selectedDosage,
  selectedFrequency,
  onComplete,
}) {
  const normalizedNwtime = normalizeNwtime(nwtime);
  const med = { ...selectedMedicationForTime, ...medication };
  const ordnumber = med.ordernumber || med.order_number;
  const timeList = normalizedNwtime.length > 0 ? normalizedNwtime : timeInputs || [];

  if (med.prn !== '1' && timeList.length > 0 && timeList.some((t) => !t)) {
    await Swal.fire({
      icon: 'error',
      title: 'Missing Times',
      text: 'Please select all required times.',
      confirmButtonText: 'Ok',
    });
    return;
  }

  med.frequency = selectedFreq || selectedFrequency || med.med_frequency || '';
  med.dosage = med.dosage || selectedDosage || med.med_amount || '1';

  const medname = med.medicationName || med.medname;
  const medholdstartdate = med.medholdstartdate || '';
  const medholdenddate = med.medholdenddate || '';
  const medstatus = changeActiveMed ? 'Change' : 'Active';
  const reason = med.coreason || changeReason || '';
  const todaydt = formatDate(new Date());

  if (med.prn === '1') {
    med.administrationTimes = 'As needed';
    med.dates = {};
  } else {
    const newTimeArray = timeList
      .filter((t) => t)
      .map((t) => ({
        time: t,
        status: 'pending',
        dosage: parseInt(selectedDosage, 10) || 1,
      }));
    med.administrationTimes = timeList.length > 0 ? timeList.join(', ') : '';

    // The course's dates only when the clinician changed them (PP-OM-03). A
    // `new Date()` stamped on a line with no start moved its start to the day
    // of the edit, or got every time edit refused once a dose had been
    // charted on an earlier day.
    const courseRow = selectedMedicationForTime || {};
    const courseStart = changedCourseDate(med.startDate, courseRow.med_startdate ?? courseRow.startDate);
    const courseEnd = changedCourseDate(med.endDate, courseRow.endDate ?? courseRow.med_enddate);
    const todayMidnight = normalizeToMidnight(new Date());
    if (med.dates) {
      for (const dStr of Object.keys(med.dates)) {
        const d = new Date(dStr);
        if (normalizeToMidnight(d).getTime() >= todayMidnight.getTime()) {
          med.dates[dStr] = med.dates[dStr].filter(
            (slot) => slot.locked === true || slot.status === 'discontinue'
          );
        }
      }
    }

    for (let i = 0; i < FUTURE_DAYS_TO_POPULATE; i++) {
      const futureDate = new Date(todayMidnight);
      futureDate.setDate(futureDate.getDate() + i);
      if (
        med.discontinuedDate &&
        normalizeToMidnight(futureDate).getTime() >
          normalizeToMidnight(med.discontinuedDate).getTime()
      ) {
        break;
      }
      const ds = formatDateToYYYYMMDD(futureDate);
      if (!med.dates) med.dates = {};
      if (!med.dates[ds]) med.dates[ds] = [];
      newTimeArray.forEach((t) => {
        const existing = med.dates[ds].find(
          (slot) => slot.time === t.time && (slot.locked || slot.status === 'discontinue')
        );
        if (!existing) {
          const idx = med.dates[ds].findIndex((slot) => slot.time === t.time);
          if (idx === -1) {
            med.dates[ds].push({ ...t });
          } else if (!med.dates[ds][idx].locked && med.dates[ds][idx].status !== 'discontinue') {
            med.dates[ds][idx] = { ...t };
          }
        }
      });
    }

    const finish = () => onComplete?.();

    const postInsertUpdate = async (changeorder) => {
      const content = {
        MedicationAdmin: {
          API_Meth: 'InsertUpdateMedLogTimes',
          // Names the line, as MedAdmin does: without it a multi-line order
          // refused the edit (order_schedule_medication_required), and the
          // rewrite and its flags would otherwise reach every line on it.
          medentryid: med.medentryid,
          pid: allData.patient.externId,
          accountId: allData.patient.externId2,
          providerid: med.providerNpi || med.providernpi || allData.manager.externid || '',
          slotedtimes: newTimeArray,
          adminDate: todaydt,
          medname,
          ordernumber: ordnumber,
          status: medstatus,
          changeorder,
          changereason: reason,
          diagnosis: med.diagnosis || med.diagnose_code || '',
          dosage: med.dosage || selectedDosage || med.med_amount || '',
          frequency: selectedFreq || selectedFrequency || med.med_frequency || med.frequency || '',
          route: med.route || '',
          prn: med.prn === true || med.prn === '1' || med.prn === 1,
          unitType: med.unitType || med.med_doseuom || '',
          providerName: med.providerName || med.providername || '',
          providerNpi: med.providerNpi || med.providernpi || '',
          providerDea: med.providerDea || med.providerdeanumber || '',
          providerAddress: med.providerAddress || med.provideraddress || '',
          providerOffice: med.providerOffice || med.provideroffice || '',
          providerCell: med.providerCell || med.providercell || '',
          providerEmail: med.providerEmail || med.provideremail || '',
          previousProvider: med.previousProvider || '',
          pharmacyName: med.pharmacyName || med.pharmacy || '',
          pharmacyNpi: med.pharmacyNpi || '',
          pharmacyDea: med.pharmacyDea || '',
          pharmacyAddress: med.pharmacyAddress || '',
          pharmacyPhone: med.pharmacyPhone || med.pharmacyOffice || med.pharmacyoffice || '',
          pharmacyCell: med.pharmacyCell || med.pharmacycell || '',
          pharmacyEmail: med.pharmacyEmail || med.pharmacyemail || '',
          ndcnumber: med.ndcnumber || med.ndcNumber || '',
          rxnorns: med.rxnorns || med.rxNumber || '',
          quantity: med.quantity || med.total || med.available || 0,
          administration:
            med.administration === true || med.administration === 1 || med.administration === '1'
              ? 1
              : med.medadmin === 1 || med.medadmin === '1'
                ? 1
                : 0,
          prepour: med.prepour === true || med.prepour === 1 || med.prepour === '1' ? 1 : 0,
          nebulizer: med.nebulizer === true || med.nebulizer === 1 || med.nebulizer === '1' ? 1 : 0,
          rxNumber: med.rxNumber || med.rxnorns || '',
          filledDate: med.filledDate || '',
          refills: med.refills || 0,
          startDate: courseStart,
          endDate: courseEnd,
          refillReminderDate: med.refillReminderDate || '',
          expirationDate: med.expirationDate || '',
        },
      };

      showSavingSwal(
        changeorder
          ? 'Please wait while the medication change order is being processed.'
          : 'Please wait while the medication is being saved.'
      );
      try {
        const data = await postMedicationAdmin(content);
        Swal.close();
        if (data?.results === 'Insert' || data?.results === 'Updated') {
          handleSaveSuccess(
            changeorder
              ? 'Medication changes made successfully. However, meds are pending until provider signs the Pending Order.'
              : 'The medication was saved successfully.',
            changeorder ? 'Change Order Created Successfully' : 'Medication Updated',
            finish
          );
          return;
        }
        const errorMessage =
          data?.message ||
          data?.error ||
          data?.code ||
          'There was a problem saving the medication.';
        handleSaveError(changeorder ? 'Change Order Failed' : 'Save Failed', errorMessage);
      } catch (error) {
        Swal.close();
        const errorText = axios.isAxiosError(error)
          ? error.response?.data?.message || 'An error occurred while saving the medication.'
          : 'An unexpected error occurred while saving.';
        handleSaveError('Error Saving Medication', errorText);
      }
    };

    const postChangeOrder = async () => {
      const now = new Date();
      const orderDate = now.toISOString().split('T')[0];
      const orderTime = now.toTimeString().split(' ')[0];
      const content = {
        MedicationAdmin: {
          API_Meth: 'ChangeOrderForMedLogTimes',
          medentryid: med.medentryid,
          pid: allData.patient.externId,
          accountId: allData.patient.externId2,
          providerid: '123456789',
          slotedtimes: newTimeArray,
          adminDate: todaydt,
          medname,
          ordernumber: ordnumber,
          status: medstatus,
          changeorder: true,
          changereason: reason,
          frequency: selectedFreq || selectedFrequency || med.med_frequency || med.frequency || '',
          dosage: med.dosage || selectedDosage || med.med_amount || '',
          provsubaccount: allData.manager.externid,
          diagnosis: med.diagnosis || med.diagnose_code || '',
          medholdstartdate,
          medholdenddate,
          route: med.route || '',
          prn: med.prn === true || med.prn === '1' || med.prn === 1,
          unitType: med.unitType || med.med_doseuom || '',
          ndcnumber: med.ndcNumber || med.ndcnumber || '',
          rxnorns: med.rxNumber || med.rxnorns || '',
          quantity: med.tabsAvailable || med.total || med.available || 0,
          providerName: med.providerName || med.providername || '',
          providerNpi: med.providerNpi || med.providernpi || '',
          providerDea: med.providerDea || med.providerdeanumber || '',
          providerAddress: med.providerAddress || med.provideraddress || '',
          providerOffice: med.providerOffice || med.provideroffice || '',
          providerCell: med.providerCell || med.providercell || '',
          providerEmail: med.providerEmail || med.provideremail || '',
          previousProvider: med.previousProvider || '',
          pharmacyName: med.pharmacyName || med.pharmacy || '',
          pharmacyNpi: med.pharmacyNpi || '',
          pharmacyDea: med.pharmacyDea || med.pharmacyDea || '',
          pharmacyAddress: med.pharmacyAddress || '',
          pharmacyPhone: med.pharmacyPhone || med.pharmacyOffice || med.pharmacyoffice || '',
          pharmacyCell: med.pharmacyCell || med.pharmacycell || '',
          pharmacyEmail: med.pharmacyEmail || med.pharmacyemail || '',
          orderDate,
          orderTime,
          writerName: sessionActorFrom(allData, peekSessionSelfProfile()).displayName,
          rxNumber: med.rxNumber || med.rxnorns || '',
          filledDate: med.filledDate || '',
          refills: med.refills || 0,
          startDate: courseStart,
          endDate: courseEnd,
          refillReminderDate: med.refillReminderDate || '',
          expirationDate: med.expirationDate || '',
          administration:
            med.administration === true || med.administration === 1 || med.administration === '1'
              ? 1
              : med.medadmin === 1 || med.medadmin === '1'
                ? 1
                : 0,
          prepour: med.prepour === true || med.prepour === 1 || med.prepour === '1' ? 1 : 0,
          nebulizer: med.nebulizer === true || med.nebulizer === 1 || med.nebulizer === '1' ? 1 : 0,
        },
      };

      showSavingSwal('Please wait while the medication change order is being processed.');
      try {
        const data = await postMedicationAdmin(content);
        Swal.close();
        if (data?.results === 'Insert' || data?.results === 'Updated') {
          handleSaveSuccess(
            'Medication changes made successfully. However, meds are pending until provider signs the Pending Order.',
            'Change Order Created Successfully',
            finish
          );
          return;
        }
        const errorMessage =
          data?.message ||
          (typeof data?.error === 'string' ? data.error : null) ||
          data?.code ||
          'There was a problem processing the change order.';
        handleSaveError('Change Order Failed', errorMessage);
      } catch (error) {
        Swal.close();
        const errorText = axios.isAxiosError(error)
          ? error.response?.data?.message || 'An error occurred while processing the change order.'
          : 'An unexpected error occurred while saving.';
        handleSaveError('Error Processing Change Order', errorText);
      }
    };

    if (changeActiveMed) {
      const result = await Swal.fire({
        title: 'Change Order Required',
        text:
          'You are changing Dose or Frequency of an active Medication and a New Order is required. Do you want to proceed? If not, the administrative time will be updated.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, proceed with change order',
        cancelButtonText: 'No, just update times',
        confirmButtonColor: '#0c8687',
        cancelButtonColor: '#6c757d',
      });
      if (result.isConfirmed) {
        await postChangeOrder();
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        await postInsertUpdate(false);
      }
    } else {
      await postInsertUpdate(false);
    }
  }
}
